import { IColumnHeaderParams, IPageRange } from '@aon/aon-tws-common-ui';
import { IColumn, ILibraryQuestion } from '@aon/tws-common-ui-questions/build/lib/models';
import { cloneDeep } from 'lodash';
import { Action, Reducer } from 'redux';

export interface IQuestionListState {
	questions: ILibraryQuestion[];
	loading: boolean;
	pageRange: IPageRange;
	searchText: string;
	columns: IColumn[];
	sortParams: IColumnHeaderParams;
	showVariations: boolean;
	showSelectScoredDimensionModal: boolean;
	selectedQuestion?: ILibraryQuestion;
	selectedQuestionVariationId?: number;
}

export const defaultState: IQuestionListState = {
	questions: [],
	loading: false,
	pageRange: {
		offset: 0,
		limit: 0,
	},
	searchText: '',
	columns: [],
	sortParams: {
		columns: [],
	},
	showVariations: false,
	showSelectScoredDimensionModal: false,
};

export enum ActionTypes {
	QUESTIONS_GET_REQUESTED = 'QUESTIONS_GET_REQUESTED',
	QUESTIONS_GET_SUCCEEDED = 'QUESTIONS_GET_SUCCEEDED',
	QUESTIONS_GET_FAILED = 'QUESTIONS_GET_FAILED',
	QUESTION_COLUMNS_GET_REQUESTED = 'QUESTION_COLUMNS_GET_REQUESTED',
	QUESTION_COLUMNS_GET_SUCCEEDED = 'QUESTION_COLUMNS_GET_SUCCEEDED',
	QUESTION_COLUMNS_GET_FAILED = 'QUESTION_COLUMNS_GET_FAILED',
	SHOW_ALL_VARIATIONS = 'SHOW_ALL_VARIATIONS',
	SHOW_SINGLE_VARIATION = 'SHOW_SINGLE_VARIATION',
	UPDATE_COLUMNS = 'UPDATE_COLUMNS',
	QUESTION_LANGUAGES_BG_GET_SUCCEEDED = 'QUESTION_LANGUAGES_BG_GET_SUCCEEDED',
	QUESTION_TAGS_BG_GET_SUCCEEDED = 'QUESTION_TAGS_BG_GET_SUCCEEDED',
	QUESTION_TEMPLATES_BG_GET_SUCCEEDED = 'QUESTION_TEMPLATES_BG_GET_SUCCEEDED',
	QUESTION_BENCHMARKS_BG_GET_SUCCEEDED = 'QUESTION_BENCHMARKS_BG_GET_SUCCEEDED',
	TOGGLE_SELECT_SCORED_DIMENSION_MODAL = 'TOGGLE_SELECT_SCORED_DIMENSION_MODAL',
	SET_SELECTED_QUESTION = 'SET_SELECTED_QUESTION',
}

export interface IQuestionsGetRequested extends Action {
	type: ActionTypes.QUESTIONS_GET_REQUESTED;
}
export interface IQuestionsGetSucceeded extends Action {
	type: ActionTypes.QUESTIONS_GET_SUCCEEDED;
	payload: {
		questions: ILibraryQuestion[];
		columns: IColumn[];
	};
}
export interface IQuestionsGetFailed extends Action {
	type: ActionTypes.QUESTIONS_GET_FAILED;
	payload: string;
}
export interface IShowAllVariations extends Action {
	type: ActionTypes.SHOW_ALL_VARIATIONS;
	payload: boolean;
}
export interface IShowSingleVariation extends Action {
	type: ActionTypes.SHOW_SINGLE_VARIATION;
	payload: number;
}
export interface IUpdateColumns extends Action {
	type: ActionTypes.UPDATE_COLUMNS;
	payload: IColumn[];
}
export interface IQuestionLanguagesBackgroundGetSucceeded extends Action {
	type: ActionTypes.QUESTION_LANGUAGES_BG_GET_SUCCEEDED;
	payload: ILibraryQuestion[];
}
export interface IQuestionTagsBackgroundGetSucceeded extends Action {
	type: ActionTypes.QUESTION_TAGS_BG_GET_SUCCEEDED;
	payload: ILibraryQuestion[];
}
export interface IQuestionTemplatesBackgroundGetSucceeded extends Action {
	type: ActionTypes.QUESTION_TEMPLATES_BG_GET_SUCCEEDED;
	payload: ILibraryQuestion[];
}
export interface IQuestionBenchmarksBackgroundGetSucceeded extends Action {
	type: ActionTypes.QUESTION_BENCHMARKS_BG_GET_SUCCEEDED;
	payload: ILibraryQuestion[];
}
export interface IToggleSelectScoredDimensionModal extends Action {
	type: ActionTypes.TOGGLE_SELECT_SCORED_DIMENSION_MODAL;
	payload: boolean;
}
export interface ISetSelectedQuestion extends Action {
	type: ActionTypes.SET_SELECTED_QUESTION;
	payload: { question: ILibraryQuestion | undefined; questionVariationId: number | undefined };
}

type KnownActions =
	| IQuestionsGetRequested
	| IQuestionsGetSucceeded
	| IQuestionsGetFailed
	| IShowAllVariations
	| IShowSingleVariation
	| IUpdateColumns
	| IQuestionLanguagesBackgroundGetSucceeded
	| IQuestionTagsBackgroundGetSucceeded
	| IQuestionTemplatesBackgroundGetSucceeded
	| IQuestionBenchmarksBackgroundGetSucceeded
	| IToggleSelectScoredDimensionModal
	| ISetSelectedQuestion;

export const reducer: Reducer<IQuestionListState, KnownActions> = (state: IQuestionListState = defaultState, action: KnownActions) => {
	switch (action.type) {
		// QUESTIONS
		case ActionTypes.QUESTIONS_GET_REQUESTED:
			return { ...state, loading: true };

		case ActionTypes.QUESTIONS_GET_SUCCEEDED: {
			return {
				...state,
				columns: action.payload.columns,
				questions: action.payload.questions,
				loading: false,
			};
		}

		case ActionTypes.QUESTIONS_GET_FAILED:
			return { ...state, loading: false };

		case ActionTypes.QUESTION_LANGUAGES_BG_GET_SUCCEEDED:
			return { ...state, questions: action.payload };

		case ActionTypes.QUESTION_TAGS_BG_GET_SUCCEEDED:
			return { ...state, questions: action.payload };

		case ActionTypes.QUESTION_TEMPLATES_BG_GET_SUCCEEDED:
			return { ...state, questions: action.payload };

		case ActionTypes.QUESTION_BENCHMARKS_BG_GET_SUCCEEDED:
			return { ...state, questions: action.payload };

		// VARIATIONS
		case ActionTypes.SHOW_ALL_VARIATIONS: {
			const updatedQuestions = state.questions.map((question: ILibraryQuestion) => {
				return { ...question, showVariations: action.payload };
			});
			return { ...state, questions: updatedQuestions };
		}
		case ActionTypes.SHOW_SINGLE_VARIATION: {
			const updatedQuestions = state.questions.map((question: ILibraryQuestion) => {
				if (action.payload !== question.questionId) {
					return question;
				}
				const questionClone = { ...question };
				questionClone.showVariations = !questionClone.showVariations;
				return questionClone;
			});
			return { ...state, questions: updatedQuestions };
		}

		// COLUMNS
		case ActionTypes.UPDATE_COLUMNS: {
			const updatedColumns = cloneDeep(action.payload);
			return {
				...state,
				columns: updatedColumns || [],
				sortParams: {
					...state.sortParams,
				},
			};
		}
		case ActionTypes.TOGGLE_SELECT_SCORED_DIMENSION_MODAL:
			return {
				...state,
				showSelectScoredDimensionModal: action.payload,
			};

		case ActionTypes.SET_SELECTED_QUESTION:
			return {
				...state,
				selectedQuestion: action.payload.question,
				selectedQuestionVariationId: action.payload.questionVariationId,
			};

		default:
			return state;
	}
};
